/**
 * The ONLY interpreter of RuntimeTheme for the component library. It maps semantic
 * theme tokens to CSS custom properties (consumed by styles.css) and to button/navbar
 * modifier classes. Components never read DESIGN_SYSTEMS or the schema Theme — they
 * pass their RuntimeTheme here. See DESIGN_SYSTEM_RENDERING.md.
 *
 * Beyond the raw tokens, this emits a full "personality" set of variables per design
 * system (typography weight/tracking, card/shadow/overlay treatments, container width,
 * hero height) so Modern / Classic / Premium feel genuinely distinct — not just
 * recolored. This is still pure interpretation of the frozen RuntimeTheme.
 */
import type { CSSProperties } from 'react'
import type { RuntimeTheme } from '../../../runtime/types'

// Exported so the Manual Editor can translate theme values → the SAME CSS var values the
// component styles consume (Phase 11.6, Part A). Values are unchanged.
export const SPACING_Y: Record<string, string> = { compact: '3.5rem', normal: '5rem', spacious: '7rem' }
const GAP: Record<string, string> = { compact: '1.25rem', normal: '1.75rem', spacious: '2.25rem' }
export const RADIUS: Record<string, string> = { none: '0px', small: '6px', medium: '12px', large: '18px', full: '9999px' }
export const RADIUS_LG: Record<string, string> = { none: '0px', small: '10px', medium: '18px', large: '26px', full: '9999px' }

const FONT_STACK: Record<string, string> = {
  Inter: "'Inter', system-ui, sans-serif",
  Poppins: "'Poppins', system-ui, sans-serif",
  'Playfair Display': "'Playfair Display', Georgia, serif",
  Lora: "'Lora', Georgia, serif",
  Fraunces: "'Fraunces', Georgia, serif",
  Montserrat: "'Montserrat', system-ui, sans-serif",
}

/** Per-design-system visual personality (typography, cards, overlays, rhythm). */
const PERSONALITY: Record<string, Record<string, string>> = {
  modern: {
    '--sb-heading-weight': '700',
    '--sb-heading-tracking': '-0.025em',
    '--sb-heading-leading': '1.08',
    '--sb-eyebrow-tracking': '0.14em',
    '--sb-card-border': '1px solid rgba(15,23,42,0.06)',
    '--sb-card-shadow': '0 1px 2px rgba(15,23,42,0.04), 0 10px 30px rgba(15,23,42,0.05)',
    '--sb-card-shadow-hover': '0 18px 48px rgba(15,23,42,0.12)',
    '--sb-btn-shadow': '0 8px 22px color-mix(in srgb, var(--sb-primary) 28%, transparent)',
    '--sb-overlay': 'linear-gradient(180deg, rgba(15,23,42,0.25), rgba(15,23,42,0.62))',
    '--sb-container': '1200px',
    '--sb-bg-alt': '#f8fafc',
    '--sb-hero-min': '80vh',
  },
  classic: {
    '--sb-heading-weight': '600',
    '--sb-heading-tracking': '0em',
    '--sb-heading-leading': '1.16',
    '--sb-eyebrow-tracking': '0.22em',
    '--sb-card-border': '1px solid rgba(23,20,15,0.14)',
    '--sb-card-shadow': 'none',
    '--sb-card-shadow-hover': '0 12px 34px rgba(23,20,15,0.09)',
    '--sb-btn-shadow': 'none',
    '--sb-overlay': 'linear-gradient(180deg, rgba(28,24,18,0.35), rgba(28,24,18,0.66))',
    '--sb-container': '1120px',
    '--sb-bg-alt': '#faf8f3',
    '--sb-hero-min': '74vh',
  },
  premium: {
    '--sb-heading-weight': '600',
    '--sb-heading-tracking': '-0.01em',
    '--sb-heading-leading': '1.04',
    '--sb-eyebrow-tracking': '0.3em',
    '--sb-card-border': '1px solid rgba(15,23,42,0.10)',
    '--sb-card-shadow': '0 2px 6px rgba(15,23,42,0.05)',
    '--sb-card-shadow-hover': '0 28px 70px rgba(15,23,42,0.16)',
    '--sb-btn-shadow': 'none',
    '--sb-overlay': 'linear-gradient(180deg, rgba(8,8,10,0.30), rgba(8,8,10,0.72))',
    '--sb-container': '1280px',
    '--sb-bg-alt': '#f6f5f2',
    '--sb-hero-min': '88vh',
  },
}

export function fontStack(font: string): string {
  return FONT_STACK[font] ?? `'${font}', system-ui, sans-serif`
}

/**
 * The baseline theme that, when NOT overridden, renders the pixel-perfect reference
 * default. Un-edited fields must NOT emit a `--sb-*` var so the foundation reference
 * fallback (in styles.css) shows through — the `borderRadius`/`spacing` values here are
 * just comparison sentinels, never emitted directly. (Phase 11.6 Part B.)
 */
export const REFERENCE_THEME = {
  primaryColor: '#1c2e73',
  headingFont: 'Inter',
  bodyFont: 'Inter',
  borderRadius: 'medium',
  spacing: 'normal',
} as const

/**
 * The default `branding.brandColor` a freshly generated site carries (see the mock
 * provider's `initialBrandColor`) — the comparison sentinel for {@link themeOverrideVars}.
 * `brandColor` is independent of `REFERENCE_THEME.primaryColor`: they own separate CSS
 * variables (`--sb-brand` vs `--sb-primary`) and are never derived from one another.
 */
export const REFERENCE_BRAND_COLOR = '#000000'

/** The minimal theme shape needed to diff against {@link REFERENCE_THEME}. */
interface ThemeLike {
  readonly primaryColor: string
  readonly typography: { readonly headingFont: string; readonly bodyFont: string }
  readonly borderRadius: string
  readonly spacing: string
}

/**
 * `--sb-*` CSS vars for ONLY the theme/brand fields that DIFFER from their reference
 * defaults. Un-edited fields emit nothing, so the reference default renders. This makes a
 * saved ("manual") site reload identically to the live editor: reference default + just
 * the user's edited dimensions. `primaryColor` → `--sb-primary` and `brandColor` →
 * `--sb-brand` are independent checks — editing one never emits the other's variable.
 */
export function themeOverrideVars(theme: ThemeLike, brandColor?: string): Record<string, string> {
  const vars: Record<string, string> = {}
  if (theme.primaryColor !== REFERENCE_THEME.primaryColor) vars['--sb-primary'] = theme.primaryColor
  if (brandColor !== undefined && brandColor !== REFERENCE_BRAND_COLOR) vars['--sb-brand'] = brandColor
  if (theme.typography.headingFont !== REFERENCE_THEME.headingFont)
    vars['--sb-heading-font'] = fontStack(theme.typography.headingFont)
  if (theme.typography.bodyFont !== REFERENCE_THEME.bodyFont)
    vars['--sb-body-font'] = fontStack(theme.typography.bodyFont)
  if (theme.borderRadius !== REFERENCE_THEME.borderRadius) {
    vars['--sb-radius'] = RADIUS[theme.borderRadius]
    vars['--sb-radius-lg'] = RADIUS_LG[theme.borderRadius]
  }
  if (theme.spacing !== REFERENCE_THEME.spacing) vars['--sb-section-y'] = SPACING_Y[theme.spacing]
  return vars
}

/** RuntimeTheme → CSS custom properties applied at a section root's `style`. */
export function themeVars(theme: RuntimeTheme): CSSProperties {
  const personality = PERSONALITY[theme.designSystem] ?? PERSONALITY.modern
  const vars: Record<string, string> = {
    '--sb-primary': theme.primaryColor,
    '--sb-brand': theme.brandColor,
    '--sb-radius': RADIUS[theme.borderRadius] ?? '12px',
    '--sb-radius-lg': RADIUS_LG[theme.borderRadius] ?? '18px',
    '--sb-section-y': SPACING_Y[theme.spacing] ?? '5rem',
    '--sb-gap': GAP[theme.spacing] ?? '1.75rem',
    '--sb-heading-font': fontStack(theme.typography.headingFont),
    '--sb-body-font': fontStack(theme.typography.bodyFont),
    ...personality,
  }
  // CSS custom properties are not part of the typed CSSProperties keys; this is the
  // single, standard boundary cast for style variables (no `any`).
  return vars as CSSProperties
}

/** Button classes for the theme's button style. */
export function buttonClass(theme: RuntimeTheme): string {
  return `sb-btn sb-btn--${theme.buttonStyle}`
}

/** Navbar root modifier class for the theme's navigation style. */
export function navbarRootClass(theme: RuntimeTheme): string {
  return `sb-navbar-root sb-navbar-root--${theme.navigationStyle}`
}
